import React from "react";
import { ArrowUpRight, Scale, Shield, Zap } from "lucide-react";

const PARTNER_URL = "https://privatecourt.ai/";

const trackClick = (placement) => {
    try {
        window.gtag && window.gtag("event", "sponsored_click", { placement, partner: "privatecourt.ai" });
    } catch { /* noop */ }
};

/**
 * SponsoredCard — native ad for partner PrivateCourt.ai.
 * Variants: "sidebar" (narrow), "inline" (wide banner), "compact" (small footer).
 */
export default function SponsoredCard({ variant = "sidebar", placement = "generic" }) {
    const handleClick = () => trackClick(placement);

    if (variant === "inline") {
        return (
            <section className="bg-[var(--np-brand)] text-white relative overflow-hidden" data-testid={`sponsored-inline-${placement}`}>
                <div className="absolute top-3 right-5 text-[0.55rem] uppercase tracking-[0.2em] text-white/40">Sponsored</div>
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="np-label mb-3" style={{ color: "rgba(255,255,255,0.55)" }}>
                            Partner · <span className="text-[var(--np-accent)]">PrivateCourt.ai</span>
                        </div>
                        <h3 className="font-serif text-3xl sm:text-4xl leading-tight mb-4 font-medium">
                            Settle your dispute <em className="not-italic text-[var(--np-accent)]">online</em>, without stepping into court.
                        </h3>
                        <p className="text-white/70 max-w-xl leading-relaxed mb-5">
                            PrivateCourt is an Online Dispute Resolution platform for civil, commercial and
                            consumer disputes. Resolve from anywhere, on your schedule — no courtroom, no travel.
                        </p>
                        <a
                            href={PARTNER_URL}
                            target="_blank"
                            rel="sponsored noopener noreferrer"
                            onClick={handleClick}
                            className="inline-flex items-center gap-2 bg-[var(--np-accent)] hover:bg-[var(--np-accent-hover)] text-white px-5 py-3 font-medium transition-colors"
                            data-testid={`sponsored-cta-${placement}`}
                        >
                            Visit PrivateCourt.ai <ArrowUpRight size={16} />
                        </a>
                    </div>
                    <div className="lg:col-span-5">
                        <ul className="space-y-3 text-white/80 text-sm">
                            <li className="flex items-start gap-3"><Zap size={16} className="shrink-0 mt-0.5 text-[var(--np-accent)]" /> Fully online — notice, mediation and arbitration in one place</li>
                            <li className="flex items-start gap-3"><Shield size={16} className="shrink-0 mt-0.5 text-[var(--np-accent)]" /> Neutral mediators and arbitrators across India</li>
                            <li className="flex items-start gap-3"><Scale size={16} className="shrink-0 mt-0.5 text-[var(--np-accent)]" /> Built for civil, commercial and consumer matters</li>
                        </ul>
                    </div>
                </div>
            </section>
        );
    }

    if (variant === "compact") {
        return (
            <a
                href={PARTNER_URL}
                target="_blank"
                rel="sponsored noopener noreferrer"
                onClick={handleClick}
                className="block border border-[var(--np-border)] bg-white p-4 hover:border-[var(--np-text)] transition-colors group"
                data-testid={`sponsored-compact-${placement}`}
            >
                <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="np-label" style={{ fontSize: "0.55rem" }}>Sponsored · PrivateCourt.ai</div>
                    <ArrowUpRight size={12} className="text-[var(--np-text-muted)] group-hover:text-[var(--np-accent)] transition-colors" />
                </div>
                <div className="font-serif text-base text-[var(--np-text)] leading-tight mb-1.5">
                    Resolve disputes online with PrivateCourt
                </div>
                <div className="text-xs text-[var(--np-text-2)] leading-relaxed">
                    Online Dispute Resolution for civil, commercial and consumer matters.
                </div>
            </a>
        );
    }

    // Default: sidebar variant
    return (
        <a
            href={PARTNER_URL}
            target="_blank"
            rel="sponsored noopener noreferrer"
            onClick={handleClick}
            className="block bg-[var(--np-bg-panel)] border border-[var(--np-border)] p-6 hover:border-[var(--np-text)] transition-all group relative"
            data-testid={`sponsored-sidebar-${placement}`}
        >
            <div className="np-label mb-3 flex items-center justify-between">
                <span>Sponsored</span>
                <span className="text-[var(--np-accent)]" style={{ fontSize: "0.6rem" }}>PrivateCourt.ai</span>
            </div>
            <div className="flex items-center gap-2 mb-3">
                <div className="h-8 w-8 flex items-center justify-center bg-[var(--np-brand)] text-white shrink-0">
                    <Scale size={14} strokeWidth={1.5} />
                </div>
                <div className="font-serif text-xl leading-tight text-[var(--np-text)]">
                    Settle your dispute online
                </div>
            </div>
            <p className="text-sm text-[var(--np-text-2)] leading-relaxed mb-4">
                PrivateCourt is an Online Dispute Resolution platform for civil, commercial
                and consumer disputes — resolve without stepping into a courtroom.
            </p>
            <ul className="space-y-1.5 mb-5 text-xs text-[var(--np-text-2)]">
                <li className="flex items-start gap-2"><Zap size={12} className="shrink-0 mt-0.5 text-[var(--np-accent)]" /> Fully online notice, mediation & arbitration</li>
                <li className="flex items-start gap-2"><Shield size={12} className="shrink-0 mt-0.5 text-[var(--np-accent)]" /> Neutral mediators and arbitrators</li>
                <li className="flex items-start gap-2"><Scale size={12} className="shrink-0 mt-0.5 text-[var(--np-accent)]" /> Civil, commercial & consumer matters</li>
            </ul>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--np-brand)] group-hover:text-[var(--np-accent)] transition-colors">
                Visit PrivateCourt.ai <ArrowUpRight size={13} />
            </span>
        </a>
    );
}
